import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '../contexts/auth-context';
import { apiErrorMessage } from '../lib/api';
import { notifyError, notifySuccess } from '../lib/toast';
import { Logo } from '../components/shared/Logo';
import { Icon } from '../components/shared/Icons';
import { Input } from '../components/shared/Input';
import { Field } from '../components/shared/Field';
import { Button } from '../components/shared/Button';
import { Spinner } from '../components/shared/Spinner';
import { checkInvite } from '../services/invites.service';

// Form-only schema: just password + confirm. The invite token rides on the URL; the user's
// name was set by the admin on the invite row and is NOT collected here (2026-06-18 product
// call — see invites migration). We render the admin-set name as a read-only display above
// the password fields so the invitee sees their identity but can't override it.
const signupFormSchema = z
  .object({
    password: z.string().min(8, 'At least 8 characters').max(72),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

type SignupFormValues = z.infer<typeof signupFormSchema>;

export function SignupPage() {
  const { user, signup } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  // Look up the invite on mount so we can greet the user by name + show which federation
  // they're joining. Disabled when the URL has no token (that branch renders the
  // "incomplete link" panel and never asks for a name to begin with).
  const inviteQuery = useQuery({
    queryKey: ['invite-check', token],
    queryFn: () => checkInvite(token),
    enabled: Boolean(token),
    retry: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: { password: '', confirm: '' },
  });

  if (user) return <Navigate to="/" replace />;

  const onSubmit = handleSubmit(async (values) => {
    try {
      // The server reads full_name from the invite row; the form only carries the password.
      await signup({ invite_token: token, password: values.password });
      notifySuccess('Account created — welcome to ProRef');
      navigate('/', { replace: true });
    } catch (err) {
      notifyError(err, 'Could not create your account');
    }
  });

  // Render the error branch from the check call as its own "invite is no longer valid"
  // panel — the API distinguishes 404 (unknown) from 410 (used/expired) so we can show
  // the right copy without exposing whether the token EVER existed. The catch-all phrasing
  // covers all three sub-cases without leaking the specific reason.
  const checkError = inviteQuery.isError ? apiErrorMessage(inviteQuery.error, 'Invite is no longer valid') : null;

  return (
    <div className="grid min-h-screen place-items-center bg-bg p-6">
      <div className="w-full max-w-[400px]">
        <div className="mb-7 flex justify-center">
          <Logo size={22} />
        </div>

        {!token ? (
          // No token → the link is malformed; there's no public sign-up to fall back to.
          <div className="rounded border border-line bg-surface p-6 text-center shadow-1">
            <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-sm bg-accent-tint text-accent">
              <Icon.lock className="w-[18px] h-[18px]" />
            </div>
            <h2 className="text-lg text-ink">This invite link is incomplete</h2>
            <p className="mt-1.5 text-[13px] text-muted">
              The signup link is missing its invite token. Ask your federation administrator to resend it.
            </p>
            <Link to="/login" className="mt-4 inline-block text-[13px] font-medium text-accent">
              Back to sign in
            </Link>
          </div>
        ) : checkError ? (
          // The token exists in some prior state (or never existed) — the admin needs to
          // resend. We don't surface which sub-case it is (used vs expired vs unknown) to
          // avoid being a token-status oracle; the message is generic on purpose.
          <div className="rounded border border-line bg-surface p-6 text-center shadow-1">
            <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-sm bg-danger-tint text-danger">
              <Icon.lock className="w-[18px] h-[18px]" />
            </div>
            <h2 className="text-lg text-ink">This invite is no longer usable</h2>
            <p className="mt-1.5 text-[13px] text-muted">
              The link may have expired or already been used. Ask your federation administrator to send a fresh one.
            </p>
            <Link to="/login" className="mt-4 inline-block text-[13px] font-medium text-accent">
              Back to sign in
            </Link>
          </div>
        ) : inviteQuery.isLoading || !inviteQuery.data ? (
          // Brief spinner while the check call resolves. The form would otherwise flash
          // without the name — small UX trade for a single roundtrip on mount.
          <div className="grid place-items-center rounded border border-line bg-surface p-12 shadow-1">
            <Spinner className="w-5 h-5 text-muted" />
          </div>
        ) : (
          <div className="rounded border border-line bg-surface p-6 shadow-1">
            <h2 className="text-xl text-ink">
              Welcome to ProRef, {inviteQuery.data.full_name.split(' ')[0]}
            </h2>
            <p className="mb-5 mt-1 text-[13px] text-muted">
              Set a password to finish joining{' '}
              {inviteQuery.data.federation_name && (
                <span className="font-medium text-ink-2">{inviteQuery.data.federation_name}</span>
              )}
              {inviteQuery.data.federation_name && inviteQuery.data.sport_name && ' · '}
              {inviteQuery.data.sport_name && (
                <span className="font-medium text-ink-2">{inviteQuery.data.sport_name}</span>
              )}
              .
            </p>

            {/* Identity card — read-only so the invitee sees who they're being onboarded as
                without the form accepting any overrides (the admin is the source of truth). */}
            <div className="mb-4 rounded-sm border border-line-2 bg-bg-2 p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted">Joining as</div>
              <div className="mt-0.5 text-sm font-medium text-ink">{inviteQuery.data.full_name}</div>
              <div className="text-[12px] text-muted">{inviteQuery.data.email}</div>
            </div>

            <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
              <Field label="Password" htmlFor="password" error={errors.password?.message}>
                <Input id="password" type="password" autoComplete="new-password" placeholder="At least 8 characters" {...register('password')} />
              </Field>
              <Field label="Confirm password" htmlFor="confirm" error={errors.confirm?.message}>
                <Input id="confirm" type="password" autoComplete="new-password" placeholder="••••••••" {...register('confirm')} />
              </Field>

              <Button type="submit" variant="accent" size="lg" block disabled={isSubmitting} className="mt-1">
                {isSubmitting ? <Spinner className="w-4 h-4" /> : <>Create account <Icon.arrow className="w-3.5 h-3.5" /></>}
              </Button>
            </form>

            <p className="mt-4 text-center text-[12px] text-muted">
              Already set up?{' '}
              <Link to="/login" className="font-medium text-accent">
                Sign in
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
